import React, { useState } from 'react';

import { useQueryClient } from 'react-query';

import { useBaseTable } from 'hooks/useBaseTable';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useMutationDeleteRiskRegister } from 'api/mutations/deleteRiskRegister.mutate';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import Icon from 'components/common/Icons/icon';

import { CustomizedButton, CustomizedConfirmDeleteModal, CustomizedTooltip } from 'components/common';

export const DeleteRiskRegisterButton = ({ table }: { table: ReturnType<typeof useBaseTable>['table'] }) => {
    const [show, setShow] = useState<boolean>(false);
    const mutation = useMutationDeleteRiskRegister();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { handleSnackBar } = useCustomSnackBar();
    const queryClient = useQueryClient();
    const selectedCount = Object.keys(table.rowSelection).map((id) => id).length;
    const shouldShowButton = selectedCount > 1;
    const handleDeleteRisks = () => {
        const riskId =
            table.tableRef.current?.table.getSelectedRowModel().flatRows.map((item) => item.original.id) ?? [];
        const riskNames =
            table.tableRef.current?.table.getSelectedRowModel().flatRows.map((item) => item.original.name) ?? [];
        sendEventWithDimensions({
            action: 'Delete Multiple Risks',
            label: riskNames.join(', '),
            category: 'Risk Register',
        });
        mutation.mutate(
            {
                riskId,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('getRiskRegister').then(() => {
                        handleSnackBar(`Risk deleted successfully`, 'success');
                        table.setRowSelection({});
                    });
                },
                onError: (error) => DEFAULT_ERROR({ error, handleError: handleSnackBar }),
            },
        );
        setShow(false);
    };

    return shouldShowButton ? (
        <>
            <CustomizedTooltip
                tooltipPosition={TooltipPosition.Top}
                tooltipContent={`Delete Risk`}
                triggerElement={
                    <CustomizedButton
                        startIcon={<Icon name={'delete-icon'} color={'#cc0000'} />}
                        color={'error'}
                        onClick={() => setShow(true)}
                        variant={'outlined'}
                    >
                        Delete Selected
                    </CustomizedButton>
                }
            />
            <CustomizedConfirmDeleteModal
                isShowModal={show}
                hideModal={() => setShow(false)}
                text={`Are you sure you want to delete selected ${selectedCount} risk items?`}
                onConfirm={handleDeleteRisks}
            />
        </>
    ) : null;
};
